import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls';

import { buildKey } from './buildKey';
import { DISMISS_STATE_KIND } from './DISMISS_STATE_KIND';
import type { DismissState } from './DismissState';
import { reportUnexpectedDismissState } from './reportUnexpectedDismissState';

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const isFiniteNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

// Returns the value typed as number when valid, or null otherwise. Wrapping
// validation + typing in one place lets callers use positive narrowing
// (`if (x === null) return null`) for both required and optional timestamps.
const requireFiniteNumber = (value: unknown): number | null =>
	isFiniteNumber(value) ? value : null;

// Like requireFiniteNumber but allows the field to be absent. Returns:
//   - undefined when the field is missing (a valid optional state),
//   - the number when present and finite,
//   - null when present but not a finite number (invalid persisted state).
const optionalFiniteNumber = (value: unknown): number | undefined | null => {
	if (value === undefined) {
		return undefined;
	}
	return isFiniteNumber(value) ? value : null;
};

const parseDismissState = (value: unknown): DismissState | null => {
	if (!isRecord(value) || typeof value.kind !== 'string') {
		// Unrecognized shape: treat as no persisted state (fail-open).
		return null;
	}

	switch (value.kind) {
		case DISMISS_STATE_KIND.SEEN: {
			const firstViewedAt = requireFiniteNumber(value.firstViewedAt);
			if (firstViewedAt === null) {
				return null;
			}
			return { kind: DISMISS_STATE_KIND.SEEN, firstViewedAt };
		}

		case DISMISS_STATE_KIND.TIMER_RUNNING: {
			const firstViewedAt = requireFiniteNumber(value.firstViewedAt);
			const sourcesClickedAt = requireFiniteNumber(value.sourcesClickedAt);
			if (firstViewedAt === null || sourcesClickedAt === null) {
				return null;
			}
			return { kind: DISMISS_STATE_KIND.TIMER_RUNNING, firstViewedAt, sourcesClickedAt };
		}

		case DISMISS_STATE_KIND.DISMISSED: {
			const firstViewedAt = requireFiniteNumber(value.firstViewedAt);
			const dismissedAt = requireFiniteNumber(value.dismissedAt);
			const sourcesClickedAt = optionalFiniteNumber(value.sourcesClickedAt);
			if (firstViewedAt === null || dismissedAt === null || sourcesClickedAt === null) {
				return null;
			}
			return {
				kind: DISMISS_STATE_KIND.DISMISSED,
				firstViewedAt,
				sourcesClickedAt,
				dismissedAt,
			};
		}

		default:
			// Unknown kind means persisted data is invalid, so we ignore it and treat as no state.
			reportUnexpectedDismissState({
				functionName: 'parseDismissState',
				kind: value.kind,
			});
			return null;
	}
};

export const getLocalState = (aaid: string): DismissState | null => {
	// SSR guard: localStorage is browser-only. This is read at render time via
	// `getGhostIconsRenderMode` inside a `useMemo` (see useGhostIconsPillState),
	// which also executes on the server, so short-circuit before touching
	// AtlBrowserStorageLocal rather than relying solely on the try/catch below.
	if (typeof window === 'undefined') {
		return null;
	}
	const key = buildKey(aaid);
	try {
		const rawState = AtlBrowserStorageLocal.getItem(key);
		if (rawState === null) {
			return null;
		}

		return parseDismissState(JSON.parse(rawState));
	} catch {
		// AtlBrowserStorageLocal handles cookie-preference categorisation, but
		// the underlying storage call can still throw (SSR contexts where
		// localStorage is undefined, corrupt JSON, quota limits). Treat any
		// read failure as no state.
		return null;
	}
};
